// Email/password/signup validation (contract 06 §3, ported 1:1). Returns `validation.*` i18n
// KEYS (never English) so the component resolves copy. "Empty is valid" for live UI: the field
// only fails once the user has typed something invalid; required-ness is checked at submit.
import type { SignupPayload } from '../models/auth.types'

// Domain labels exclude `.` so the `\.` separators are unambiguous — no super-linear backtracking.
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/
// 8–16 chars with at least one letter, one digit and one symbol.
const PASS_RE = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,16}$/

const REQUIRED = 'validation.required'

// Free mail providers a signup may not use — the account is a company's (v3
// `validators/PersonalEmailDomains.ts`).
const PERSONAL_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'ymail.com',
  'outlook.com', 'outlook.co.uk', 'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'live.com',
  'msn.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'protonmail.com', 'proton.me', 'pm.me',
  'gmx.com', 'gmx.net', 'mail.com', 'yandex.com', 'yandex.ru', 'zoho.com', 'qq.com', '163.com',
  '126.com', 'naver.com', 'rediffmail.com', 'inbox.com', 'fastmail.com', 'tutanota.com', 'hey.com',
])

export type SignupField =
  'name' | 'company_name' | 'email' | 'password' | 'confirm_password' | 'terms'
export interface SignupValidation {
  valid: boolean
  /** Per-field i18n key (`validation.*`), only for the fields that failed. */
  fieldErrors: Partial<Record<SignupField, string>>
}

export class AuthValidator {
  /** Live-UI email check: empty is valid; otherwise must match. */
  static isEmailValid(email: string): boolean {
    return email === '' || EMAIL_RE.test(email)
  }

  /** Whether the address is on a free mail provider (Gmail, Outlook, …) rather than a work domain. */
  static isPersonalEmail(email: string): boolean {
    const value = email.trim().toLowerCase()
    const at = value.lastIndexOf('@')
    return at !== -1 && PERSONAL_EMAIL_DOMAINS.has(value.slice(at + 1))
  }

  /** Live-UI password check: empty is valid; otherwise must match the rule. */
  static isPasswordValid(password: string): boolean {
    return password === '' || PASS_RE.test(password)
  }

  /** Submit-time validation → the `validation.*` msgKey for each failing field + terms. */
  validateSignup(payload: SignupPayload, termsAccepted: boolean): SignupValidation {
    const fieldErrors: Partial<Record<SignupField, string>> = {}

    if (!payload.name) fieldErrors.name = REQUIRED
    if (!payload.company_name) fieldErrors.company_name = REQUIRED

    if (!payload.email) fieldErrors.email = REQUIRED
    else if (!EMAIL_RE.test(payload.email)) fieldErrors.email = 'validation.email'
    else if (AuthValidator.isPersonalEmail(payload.email)) fieldErrors.email = 'validation.personalEmail'

    if (!payload.password) fieldErrors.password = REQUIRED
    else if (!PASS_RE.test(payload.password)) fieldErrors.password = 'validation.passwordRule'

    if (payload.confirm_password !== undefined && payload.confirm_password !== payload.password) {
      fieldErrors.confirm_password = 'validation.passwordMismatch'
    }

    if (!termsAccepted) fieldErrors.terms = REQUIRED

    return { valid: Object.keys(fieldErrors).length === 0, fieldErrors }
  }
}
